import { execCmd } from "./editorCommands";
import { aktiveAuswahl, aktivesDokument } from "../../windowManager/browserFenster/fensterRegister";

/**
 * tabulator.ts — Tab im Fließtext wie in Outlook (T-346).
 *
 * Entscheidung des Users „sowie in Outlook":
 *  - Tab am ANFANG eines Absatzes rückt den ganzen Absatz ein (Umschalt+Tab
 *    bzw. Rücktaste am Absatzanfang nimmt den Einzug wieder zurück).
 *  - Tab mitten in der Zeile fügt einen Tabulator-Abstand ein, z. B. um einen
 *    Betrag auszurichten („VSU bis ⇥ € 25.000,-"); Umschalt+Tab direkt
 *    dahinter nimmt ihn wieder heraus.
 *  - In einer Liste bleibt Tab das Einrücken des Punkts (`listenTasten.ts`).
 *
 * ══ WIE EIN TAB IN EINER MAIL ÜBERLEBT ═════════════════════════════════════
 * Ein nacktes Tabulatorzeichen fällt in HTML zu einem Leerzeichen zusammen.
 * Der Abstand steht deshalb als `<span style="white-space:pre">⇥</span>` im
 * Text — genau das legt Chromium/Edge bei `insertText("\t")` selbst an, und
 * so schreiben auch Gmail und Apple Mail ihre Tabs. Beim Versand bekommt die
 * Hülle zusätzlich `mso-tab-count` (`tabsFuerVersand`): Daran erkennt der
 * Word-Renderer von Outlook Desktop einen echten Tabulator und springt zum
 * nächsten Tabstopp (Standard 1,25 cm), statt eine feste Breite zu zeigen.
 *
 * Der Absatz-Einzug ist ein `margin-left` in pt am Absatz — kein
 * `execCommand("indent")`: Chromium legt dafür ein `<blockquote>` an, und ein
 * `<blockquote>` auf oberster Ebene hält der Sendepfad für den zitierten
 * Verlauf (`composeSendBody.findQuoteRoot`). Ein `margin-left` versteht jedes
 * Mailprogramm.
 * ═══════════════════════════════════════════════════════════════════════════
 */

/** Ein Einzug-Schritt in pt (Outlook: 36 pt ≈ 1,27 cm). */
export const EINZUG_SCHRITT_PT = 36;

const ABSATZ_TAGS = new Set(["DIV", "P", "H1", "H2", "H3", "H4", "H5", "H6", "PRE"]);
/** Blöcke, in denen kein Absatz-Einzug gesetzt wird — dort gibt es nur den Tab-Abstand. */
const SPERR_TAGS = new Set(["LI", "TD", "TH", "BLOCKQUOTE", "TABLE"]);

/**
 * Der Absatz, in dem `start` steht.
 * - `"root"`: Der Text steht direkt im Editor (die erste Zeile in Chromium).
 * - `null`: kein einrückbarer Absatz (Tabellenzelle, Zitat, Signatur-Hülle …).
 */
export function absatzVon(root: HTMLElement, start: Node): HTMLElement | "root" | null {
    let n: Node | null = start;
    while (n && n !== root) {
        if (n.nodeType === 1) {
            const el = n as HTMLElement;
            if (SPERR_TAGS.has(el.tagName)) return null;
            if (ABSATZ_TAGS.has(el.tagName)) {
                // Die Hüllen von Signatur und Zitat sind keine Absätze des Nutzers.
                if (el.hasAttribute("data-kmvs-signature") || el.hasAttribute("data-kmvs-quote")) return null;
                return el;
            }
        }
        n = n.parentNode;
    }
    return n === root ? "root" : null;
}

/**
 * Steht die Einfügemarke am Anfang ihrer Zeile — vor jedem Zeichen und jedem
 * Bild des Absatzes bzw. hinter dem letzten Zeilenumbruch (`<br>`)?
 */
export function amZeilenanfang(root: HTMLElement, marke: Range): boolean {
    const absatz = absatzVon(root, marke.startContainer);
    const block = absatz === "root" || absatz == null ? root : absatz;
    const davor = aktivesDokument().createRange();
    try {
        davor.setStart(block, 0);
        davor.setEnd(marke.startContainer, marke.startOffset);
    } catch {
        return false;
    }
    let text = "";
    const walker = document.createTreeWalker(davor.cloneContents(), NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT);
    for (let n = walker.nextNode(); n; n = walker.nextNode()) {
        if (n.nodeType === 3) text += n.textContent ?? "";
        else if ((n as Element).tagName === "BR") text = "";
        else if ((n as Element).tagName === "IMG") text += "￼";
        else if (ABSATZ_TAGS.has((n as Element).tagName) || SPERR_TAGS.has((n as Element).tagName)) text = "";
    }
    return text.replace(/[​﻿]/g, "") === "";
}

/** Aktueller Einzug eines Absatzes in pt (px werden umgerechnet). */
export function einzugPt(el: HTMLElement): number {
    const roh = el.style.marginLeft.trim();
    const m = /^(-?[\d.]+)(pt|px)?$/.exec(roh);
    if (!m) return 0;
    const wert = parseFloat(m[1]);
    if (!Number.isFinite(wert)) return 0;
    return m[2] === "px" ? wert * 0.75 : wert;
}

function setzeEinzug(el: HTMLElement, pt: number): void {
    if (pt <= 0) {
        el.style.removeProperty("margin-left");
        if (el.getAttribute("style")?.trim() === "") el.removeAttribute("style");
    } else {
        el.style.marginLeft = `${Math.round(pt * 10) / 10}pt`;
    }
}

/**
 * Rückt den Absatz um einen Schritt ein (`+1`) bzw. aus (`-1`).
 * Steht der Text direkt im Editor, wird die Zeile vorher in ein `<div>` gefasst.
 * Gibt zurück, ob sich etwas geändert hat.
 */
export function absatzEinruecken(root: HTMLElement, richtung: 1 | -1): boolean {
    const sel = aktiveAuswahl();
    if (!sel || sel.rangeCount === 0) return false;
    let absatz = absatzVon(root, sel.getRangeAt(0).startContainer);
    if (absatz === "root") {
        if (richtung < 0) return false;
        execCmd(root, "formatBlock", "div");
        const neu = aktiveAuswahl();
        absatz = neu && neu.rangeCount ? absatzVon(root, neu.getRangeAt(0).startContainer) : null;
        if (absatz === "root") absatz = null;
    }
    if (!absatz) return false;
    const vorher = einzugPt(absatz);
    const nachher = Math.max(0, vorher + richtung * EINZUG_SCHRITT_PT);
    if (nachher === vorher) return false;
    setzeEinzug(absatz, nachher);
    return true;
}

/** Ist `n` die Tab-Hülle des Browsers (`<span style="white-space:pre">` nur mit Tabs)? */
export function istTabHuelle(n: Node | null): boolean {
    if (!n || n.nodeType !== 1) return false;
    const el = n as HTMLElement;
    return el.tagName === "SPAN" && /pre/.test(el.style.whiteSpace) && /^\t+$/.test(el.textContent ?? "");
}

/** Steht direkt vor der Einfügemarke ein Tabulator? */
export function tabVorCursor(marke: Range): boolean {
    const c = marke.startContainer;
    const o = marke.startOffset;
    if (c.nodeType === 3) {
        if (o > 0) return (c.textContent ?? "")[o - 1] === "\t";
        let s: Node | null = c.previousSibling;
        if (!s && istTabHuelle(c.parentNode)) return false; // am Anfang IN der Hülle
        if (!s) s = c.parentNode?.previousSibling ?? null;
        return istTabHuelle(s) || (s?.nodeType === 3 && (s.textContent ?? "").endsWith("\t"));
    }
    const s = o > 0 ? c.childNodes[o - 1] : null;
    return istTabHuelle(s) || (s?.nodeType === 3 && (s.textContent ?? "").endsWith("\t"));
}

/** Fügt an der Einfügemarke einen Tabulator ein (ersetzt eine Markierung). */
export function tabEinfuegen(root: HTMLElement): void {
    const sel = aktiveAuswahl();
    const marke = sel && sel.rangeCount ? sel.getRangeAt(0) : null;
    if (marke?.collapsed && tabVorCursor(marke)) {
        // Ein zweiter Tab direkt hinter einem ersten bekommt eine EIGENE Hülle —
        // `insertText` legte ihn in dieselbe (`\t\t`), und jeder Tab braucht
        // sein eigenes `tab-size`, um auf seinen Tabstopp zu kommen
        // (`tabAusrichtung.ts`). Nachträglich teilen bringt Strg+Z durcheinander.
        execCmd(root, "insertHTML", '<span style="white-space:pre">\t</span>');
    } else {
        // `insertText` statt eigenem DOM-Bau: bleibt rückgängig machbar (Strg+Z),
        // und Chromium/Edge legt die Tab-Hülle selbst an.
        execCmd(root, "insertText", "\t");
    }
    tabsAbsichern(root);
}

/**
 * Fasst nackte Tabulatorzeichen in eine `white-space:pre`-Hülle — für Browser,
 * die bei `insertText("\t")` keine eigene anlegen (Firefox), und für
 * eingefügten Text. Text in `<pre>` oder einer bestehenden Hülle bleibt.
 */
export function tabsAbsichern(root: ParentNode, ausserhalb?: Element | null): void {
    const doc = (root as Node).ownerDocument ?? document;
    const walker = doc.createTreeWalker(root as Node, NodeFilter.SHOW_TEXT);
    const nackt: Text[] = [];
    for (let n = walker.nextNode(); n; n = walker.nextNode()) {
        if (!(n.textContent ?? "").includes("\t")) continue;
        if (ausserhalb && ausserhalb.contains(n)) continue;
        let p: Node | null = n.parentNode;
        let geschuetzt = false;
        while (p && p !== root) {
            const el = p as HTMLElement;
            if (el.nodeType === 1 && (el.tagName === "PRE" || /pre/.test(el.style?.whiteSpace ?? ""))) {
                geschuetzt = true;
                break;
            }
            p = p.parentNode;
        }
        if (!geschuetzt) nackt.push(n as Text);
    }
    for (const t of nackt) {
        const teile = (t.textContent ?? "").split(/(\t+)/);
        const frag = doc.createDocumentFragment();
        for (const teil of teile) {
            if (!teil) continue;
            if (teil.startsWith("\t")) {
                const span = doc.createElement("span");
                span.style.whiteSpace = "pre";
                span.textContent = teil;
                frag.appendChild(span);
            } else {
                frag.appendChild(doc.createTextNode(teil));
            }
        }
        t.parentNode?.replaceChild(frag, t);
    }
}

/** Kennung eines versendeten Tabs (`tabsFuerVersand`); `tabsVorbereiten` macht daraus wieder einen Tab. */
export const TAB_KLASSE = "kmvs-tab";

/** Die im Editor gemessene Breite einer Tab-Hülle in px (`htmlFuerVersand`). */
export const TAB_BREITE_ATTR = "data-tab-breite";

/**
 * Versand: Tab-Hüllen bekommen `mso-tab-count` (Anzahl der Tabs), damit
 * Outlook Desktop sie als echte Tabulatoren setzt. Der zitierte Verlauf
 * (`ausserhalb`) bleibt unangetastet.
 *
 * ══ EXCHANGE MACHT AUS DEM TAB EIN LEERZEICHEN (26.10.2026) ════════════════
 * Exchange Online schreibt das HTML beim Senden um: `tab-stops` und
 * `mso-tab-count` bleiben, das Tab-ZEICHEN wird aber zu einem Leerzeichen,
 * am Zeilenende sogar zu einem Umbruch. Outlook Desktop richtet über die
 * Word-Angaben trotzdem aus; Outlook im Browser, das neue Outlook, iPhone
 * und Gmail zeigten nur „asdf sdfg". Hat der Editor die Breite gemessen
 * (`data-tab-breite`), geht der Tab deshalb als Kasten mit genau dieser
 * Breite hinaus — `inline-block` + `width` kennt jedes Mailprogramm —, mit
 * `mso-tab-count` daneben für Outlook Desktop. Ohne Messwert (kein Layout)
 * bleibt es beim Tab-Zeichen.
 */
export function tabsFuerVersand(root: ParentNode, ausserhalb?: Element | null): void {
    tabsAbsichern(root, ausserhalb);
    root.querySelectorAll<HTMLElement>("span").forEach((span) => {
        if (ausserhalb && ausserhalb.contains(span)) return;
        if (!istTabHuelle(span)) return;
        const anzahl = (span.textContent ?? "").length;
        const breite = parseFloat(span.getAttribute(TAB_BREITE_ATTR) ?? "");
        span.removeAttribute(TAB_BREITE_ATTR);
        if (Number.isFinite(breite) && breite > 0) {
            span.className = TAB_KLASSE;
            span.setAttribute("style", `display:inline-block;width:${breite.toFixed(2)}px;mso-tab-count:${anzahl}`);
            span.textContent = "\u00A0";
            return;
        }
        const style = (span.getAttribute("style") ?? "").replace(/;?\s*mso-tab-count\s*:[^;]*/gi, "").trim();
        span.setAttribute("style", `${style}${style && !style.endsWith(";") ? ";" : ""}mso-tab-count:${anzahl}`);
    });
}
